import pluralize from 'pluralize';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { selectedItemsSelector } from '@air/redux-selected-items';

import { GalleryItemType } from '~/components/Gallery/types';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';

export type UseSelectedItemsSummaryParams = {
  selectAllTotalCount?: number | null;
  omitBoardName?: boolean;
};

export const useSelectedItemsSummary = (params: UseSelectedItemsSummaryParams) => {
  const selectedItems = useSelector(selectedItemsSelector);
  const { crossLocationSelectionEnabled } = useFeatureFlags();
  const selectAll = useViewsSelectAllStore((state) => state.selectAll);

  const summary = useMemo(() => {
    if (selectAll && params.selectAllTotalCount != null) {
      const countText =
        params.selectAllTotalCount === 1
          ? 'All items'
          : `All ${params.selectAllTotalCount.toLocaleString()} ${pluralize('item', params.selectAllTotalCount)}`;

      return `${countText} selected`;
    }

    const hasOnlyCanvasSelected =
      selectedItems.length > 0 && selectedItems.every((item) => item.type === GalleryItemType.canvas);
    const itemLabel = hasOnlyCanvasSelected
      ? selectedItems.length === 1
        ? 'canvas'
        : 'canvases'
      : pluralize('item', selectedItems.length);

    if (!crossLocationSelectionEnabled) {
      return `${selectedItems.length} ${itemLabel} selected`;
    }

    const itemsWithUndefinedSelectedBoard = selectedItems.filter((item) => item.selectedBoard === undefined);
    const itemsWithDefinedSelectedBoard = selectedItems.filter((item) => item.selectedBoard !== undefined);
    const boardNameLookup = new Map(
      itemsWithDefinedSelectedBoard.map((item) => [item.selectedBoard?.id, item.selectedBoard?.name]),
    );
    const distinctBoardIds = new Set(
      itemsWithDefinedSelectedBoard
        .filter((item) => item.selectedBoard !== undefined)
        .map((item) => item.selectedBoard?.id),
    );

    if (itemsWithUndefinedSelectedBoard.length > 0) {
      if (distinctBoardIds.size > 0) {
        return `${selectedItems.length} ${itemLabel} selected across multiple locations`;
      } else {
        return `${selectedItems.length} ${itemLabel} selected`;
      }
    } else if (distinctBoardIds.size === 1) {
      const boardId = distinctBoardIds.values().next().value;
      const boardName = boardNameLookup.get(boardId);

      if (params.omitBoardName) {
        return `${selectedItems.length} ${itemLabel} selected`;
      }

      return `${selectedItems.length} ${itemLabel} selected ${boardName ? `from ${boardName}` : ''}`;
    } else if (distinctBoardIds.size > 1) {
      return `${selectedItems.length} ${itemLabel} selected across ${distinctBoardIds.size} boards`;
    } else {
      return `${selectedItems.length} ${itemLabel} selected`;
    }
  }, [selectedItems, crossLocationSelectionEnabled, selectAll, params.omitBoardName, params.selectAllTotalCount]);

  return { summary };
};
